// src/pages/StudentAreaPage.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Lock, Monitor, User, ShoppingBag, Shield, CheckCircle, AlertCircle, Camera } from 'lucide-react';
import './StudentAreaPage.css';

type Tab = 'profile' | 'purchases' | 'security';

interface AcademicProfile {
    phone: string | null;
    birthDate: string | null;
    state: string | null;
    city: string | null;
    intendedCourse: string | null;
}

interface Purchase {
    _id: string;
    productId: {
        _id: string;
        title: string;
        description: string;
        type: string;
        refId?: string;
    };
    accessType: 'lifetime' | 'subscription';
    expiresAt: string | null;
    createdAt: string;
    status: 'active' | 'expired' | 'expiring_soon';
    daysUntilExpiry: number | null;
}

const BR_STATES = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
    'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC',
    'SP','SE','TO'
];

const StudentAreaPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, reloadProfile } = useAuth();
    const [activeTab, setActiveTab] = useState<Tab>('profile');
    const [loading, setLoading] = useState(true);

    const [profileCompleteness, setProfileCompleteness] = useState(0);
    const [academicProfile, setAcademicProfile] = useState<AcademicProfile>({
        phone: null,
        birthDate: null,
        state: null,
        city: null,
        intendedCourse: null,
    });
    const [editingProfile, setEditingProfile] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);

    const [purchases, setPurchases] = useState<Purchase[]>([]);
    const [loadingPurchases, setLoadingPurchases] = useState(false);

    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [showPhotoMenu, setShowPhotoMenu] = useState(false);
    const photoMenuRef = useRef<HTMLDivElement>(null);

    const [, setHasAnyPurchase] = useState(false);
    const [userAuthProvider, setUserAuthProvider] = useState<string>('local');
    const [vipAccess, setVipAccess] = useState<{
        active: boolean;
        expiresAt: string | null;
    } | null>(null);

    const [mfaStatus, setMfaStatus] = useState<{ enabled: boolean; lastUsed: string | null; backupCodesRemaining: number } | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        await Promise.all([loadProfile(), loadPurchases()]);
        setLoading(false);
    }, []);

    const loadMFAStatus = useCallback(async () => {
        if (user?.role !== 'admin' && user?.role !== 'vendor') return;
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/mfa/status', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) setMfaStatus(await response.json());
        } catch (error) {
            console.error('Erro ao carregar status MFA:', error);
        }
    }, [user?.role]);

    useEffect(() => {
        loadData();
        loadMFAStatus();
    }, [loadData, loadMFAStatus]);

    // Fechar menu de foto ao clicar fora
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (photoMenuRef.current && !photoMenuRef.current.contains(e.target as Node)) {
                setShowPhotoMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const calculateLocalCompleteness = (profile: AcademicProfile, hasPhoto?: boolean) => {
        let score = 0;
        if (hasPhoto ?? !!user?.profilePhoto) score += 20;
        if (profile.phone) score += 16;
        if (profile.birthDate) score += 16;
        if (profile.state) score += 16;
        if (profile.city) score += 16;
        if (profile.intendedCourse) score += 16;
        return score;
    };

    const loadProfile = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/users/profile', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUserAuthProvider(data.authProvider || 'local');
                setProfileCompleteness(data.profileCompleteness || 0);
                setAcademicProfile({
                    phone: data.phone || null,
                    birthDate: data.birthDate ? data.birthDate.split('T')[0] : null,
                    state: data.state || null,
                    city: data.city || null,
                    intendedCourse: data.intendedCourse || null,
                });
                if (data.vipAccess) {
                    setVipAccess({
                        active: data.vipAccess.active || false,
                        expiresAt: data.vipAccess.expiresAt || null,
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao carregar perfil:', error);
        }
    };

    const loadPurchases = async () => {
        setLoadingPurchases(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/users/my-purchases', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPurchases(data.purchases);
                setHasAnyPurchase(data.purchases.length > 0);
            }
        } catch (error) {
            console.error('Erro ao carregar compras:', error);
        } finally {
            setLoadingPurchases(false);
        }
    };

    const handleSaveAcademicProfile = async () => {
        setSavingProfile(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/users/profile/academic', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(academicProfile)
            });
            if (response.ok) {
                const newCompleteness = calculateLocalCompleteness(academicProfile);
                setProfileCompleteness(newCompleteness);
                setEditingProfile(false);
                await reloadProfile();
            }
        } catch (error) {
            console.error('Erro ao salvar perfil:', error);
            alert('Erro ao salvar perfil');
        } finally {
            setSavingProfile(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Não definida';
        const date = new Date(dateString);
        const userTimezoneOffset = date.getTimezoneOffset() * 60000;
        const adjustedDate = new Date(date.getTime() + userTimezoneOffset);
        return adjustedDate.toLocaleDateString('pt-BR');
    };

    const formatVipExpiry = (expiresAt: string | null): string => {
        if (!expiresAt) return 'Vitalício';
        const date = new Date(expiresAt);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 0) return 'Expirado';
        if (diffDays === 0) return 'Expira hoje';
        if (diffDays === 1) return 'Expira amanhã';
        if (diffDays <= 7) return `Expira em ${diffDays} dias`;
        const userTimezoneOffset = date.getTimezoneOffset() * 60000;
        const adjustedDate = new Date(date.getTime() + userTimezoneOffset);
        return `Válido até ${adjustedDate.toLocaleDateString('pt-BR')}`;
    };

    const getProductTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
            deck: 'Deck',
            custom_deck: 'Deck Personalizado',
            combo: 'Combo',
            cycle: 'Ciclo',
            access_all: 'Acesso Total',
        };
        return labels[type] || type;
    };

    const getStatusBadge = (status: string) => {
        const statusMap = {
            active: { label: 'Ativo', color: '#0D3B0E' },
            expired: { label: 'Expirado', color: '#DC2626' },
            expiring_soon: { label: 'Expira em breve', color: '#FFA500' }
        };
        const s = statusMap[status as keyof typeof statusMap] || statusMap.active;
        return (
            <span className="stdpgpro-purchase-status-badge" style={{ background: s.color }}>
                {s.label}
            </span>
        );
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        if (!validTypes.includes(file.type)) { alert('Por favor, selecione uma imagem PNG, JPG ou WEBP'); return; }
        if (file.size > 5 * 1024 * 1024) { alert('Arquivo muito grande. Máximo: 5MB'); return; }
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result as string);
        reader.readAsDataURL(file);
        setShowPhotoMenu(false);
    };

    const handleUploadPhoto = async () => {
        if (!photoFile) return;
        setUploadingPhoto(true);
        try {
            const token = localStorage.getItem('accessToken');
            const formData = new FormData();
            formData.append('photo', photoFile);
            const response = await fetch('/api/users/profile/photo', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            if (response.ok) {
                await loadProfile();
                setPhotoFile(null);
                setPhotoPreview(null);
                await reloadProfile();
            } else {
                const data = await response.json();
                alert(data.message || 'Erro ao fazer upload');
            }
        } catch (error) {
            console.error('Erro ao fazer upload:', error);
            alert('Erro ao fazer upload da foto');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleRemovePhoto = async () => {
        if (!window.confirm('Deseja remover sua foto de perfil?')) return;
        setShowPhotoMenu(false);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/users/profile/photo', {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) { await loadProfile(); await reloadProfile(); }
        } catch (error) {
            console.error('Erro ao remover foto:', error);
            alert('Erro ao remover foto');
        }
    };

    const getProgressMessage = (percent: number) => {
        if (percent === 100) return 'Perfil completo!';
        if (percent >= 80) return `Quase lá — ${percent}% preenchido`;
        if (percent >= 40) return `Seu perfil está ${percent}% pronto`;
        return `Complete seu perfil (${percent}%)`;
    };

    const MFAStatusSection = () => {
        if (!mfaStatus) return null;
        if (mfaStatus.enabled) {
            return (
                <div className="stdpgpro-mfa-status-box enabled">
                    <div className="stdpgpro-mfa-status-icon"><Shield size={20} /></div>
                    <div className="stdpgpro-mfa-status-info">
                        <p className="stdpgpro-mfa-status-title">MFA Ativo</p>
                        <p className="stdpgpro-mfa-status-description">Sua conta está protegida com autenticação de dois fatores</p>
                        {mfaStatus.backupCodesRemaining > 0 && (
                            <p className="stdpgpro-mfa-status-codes">{mfaStatus.backupCodesRemaining} códigos de backup restantes</p>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div className="stdpgpro-mfa-status-box disabled">
                <div className="stdpgpro-mfa-status-icon"><AlertCircle size={20} /></div>
                <div className="stdpgpro-mfa-status-info">
                    <p className="stdpgpro-mfa-status-title">MFA Inativo</p>
                    <p className="stdpgpro-mfa-status-description">Configure o MFA para maior segurança</p>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="stdpgpro-page">
                <div className="stdpgpro-loading-container">
                    <div className="stdpgpro-spinner"></div>
                    <p>Carregando...</p>
                </div>
            </div>
        );
    }

    const currentPhoto = photoPreview || user?.profilePhoto || null;

    return (
        <div className="stdpgpro-page">

            {/* HEADER com textura */}
            <header className="stdpgpro-header">
                <button className="stdpgpro-btn-back" onClick={() => navigate('/dashboard')}>
                    <ArrowLeft size={18} />
                    Voltar
                </button>
                <div className="stdpgpro-header-logo">
                    <h1>GRAIA</h1>
                </div>
            </header>

            {/* CARD DE PERFIL FLUTUANTE */}
            <div className="stdpgpro-profile-card-wrap">
                <div className="stdpgpro-profile-card">
                    {/* Avatar com menu de edição */}
                    <div className="stdpgpro-avatar-wrap" ref={photoMenuRef}>
                        <button
                            className="stdpgpro-avatar-btn"
                            onClick={() => setShowPhotoMenu(prev => !prev)}
                            title="Editar foto"
                        >
                            {currentPhoto ? (
                                <img src={currentPhoto} alt="Foto de perfil" className="stdpgpro-avatar" />
                            ) : (
                                <div className="stdpgpro-avatar-placeholder">
                                    {user?.username.charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div className="stdpgpro-avatar-overlay">
                                <Camera size={18} />
                            </div>
                        </button>

                        {showPhotoMenu && (
                            <div className="stdpgpro-photo-menu">
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/webp"
                                    onChange={handlePhotoChange}
                                    style={{ display: 'none' }}
                                    id="photo-upload-inline"
                                />
                                <label htmlFor="photo-upload-inline" className="stdpgpro-photo-menu-item">
                                    Alterar foto
                                </label>
                                {currentPhoto && !photoFile && (
                                    <button className="stdpgpro-photo-menu-item danger" onClick={handleRemovePhoto}>
                                        Remover foto
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="stdpgpro-profile-info">
                        <h2 className="stdpgpro-profile-name">{user?.username}</h2>
                        <p className="stdpgpro-profile-email">{user?.email}</p>
                        <div className="stdpgpro-progress-wrap">
                            <div className="stdpgpro-progress-bar">
                                <div
                                    className="stdpgpro-progress-fill"
                                    style={{ width: `${profileCompleteness}%` }}
                                />
                            </div>
                            <span className="stdpgpro-progress-label">
                                {getProgressMessage(profileCompleteness)}
                            </span>
                        </div>
                    </div>

                    {photoFile && (
                        <button
                            className="stdpgpro-btn-save-photo"
                            onClick={handleUploadPhoto}
                            disabled={uploadingPhoto}
                        >
                            {uploadingPhoto ? 'Salvando...' : 'Salvar foto'}
                        </button>
                    )}
                </div>
            </div>

            {/* PILLS DE NAVEGAÇÃO */}
            <div className="stdpgpro-pills-bar">
                <div className="stdpgpro-pills">
                    <button
                        className={`stdpgpro-pill ${activeTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setActiveTab('profile')}
                    >
                        <User size={15} />
                        <span>Perfil</span>
                    </button>
                    <button
                        className={`stdpgpro-pill ${activeTab === 'purchases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('purchases')}
                    >
                        <ShoppingBag size={15} />
                        <span>Minhas Compras</span>
                    </button>
                    <button
                        className={`stdpgpro-pill ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        <Shield size={15} />
                        <span>Conta &amp; Segurança</span>
                    </button>
                </div>
            </div>

            {/* CONTEÚDO */}
            <div className="stdpgpro-content">

                {/* ABA: PERFIL */}
                {activeTab === 'profile' && (
                    <div className="stdpgpro-profile-tab">
                        <div className="stdpgpro-section-card">
                            <div className="stdpgpro-section-header">
                                <h3>Sobre Você</h3>
                                {!editingProfile && (
                                    <button className="stdpgpro-btn-edit" onClick={() => setEditingProfile(true)}>
                                        Editar
                                    </button>
                                )}
                            </div>

                            {profileCompleteness < 100 ? (
                                <div className="stdpgpro-notice">
                                    <AlertCircle size={16} />
                                    <p>{getProgressMessage(profileCompleteness)} — complete para personalizar sua experiência.</p>
                                </div>
                            ) : (
                                <div className="stdpgpro-notice success">
                                    <CheckCircle size={16} />
                                    <p>Perfil completo!</p>
                                </div>
                            )}

                            {editingProfile ? (
                                <div className="stdpgpro-edit-form">
                                    <div className="stdpgpro-form-row">
                                        <div className="stdpgpro-form-group">
                                            <label>Celular / WhatsApp</label>
                                            <input
                                                type="tel"
                                                placeholder="(00) 00000-0000"
                                                value={academicProfile.phone || ''}
                                                onChange={(e) => setAcademicProfile({ ...academicProfile, phone: e.target.value })}
                                            />
                                        </div>
                                        <div className="stdpgpro-form-group">
                                            <label>Data de Nascimento</label>
                                            <input
                                                type="date"
                                                value={academicProfile.birthDate || ''}
                                                onChange={(e) => setAcademicProfile({ ...academicProfile, birthDate: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="stdpgpro-form-row">
                                        <div className="stdpgpro-form-group">
                                            <label>Estado</label>
                                            <select
                                                value={academicProfile.state || ''}
                                                onChange={(e) => setAcademicProfile({ ...academicProfile, state: e.target.value })}
                                            >
                                                <option value="">Selecione o estado</option>
                                                {BR_STATES.map(uf => (
                                                    <option key={uf} value={uf}>{uf}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="stdpgpro-form-group">
                                            <label>Cidade</label>
                                            <input
                                                type="text"
                                                placeholder="Ex: Salvador, São Paulo..."
                                                value={academicProfile.city || ''}
                                                onChange={(e) => setAcademicProfile({ ...academicProfile, city: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="stdpgpro-form-row stdpgpro-form-row-single">
                                        <div className="stdpgpro-form-group">
                                            <label>Curso pretendido</label>
                                            <input
                                                type="text"
                                                placeholder="Ex: Medicina, Direito, Engenharia..."
                                                value={academicProfile.intendedCourse || ''}
                                                onChange={(e) => setAcademicProfile({ ...academicProfile, intendedCourse: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="stdpgpro-form-actions">
                                        <button className="stdpgpro-btn-cancel" onClick={() => setEditingProfile(false)}>
                                            Cancelar
                                        </button>
                                        <button
                                            className="stdpgpro-btn-save"
                                            onClick={handleSaveAcademicProfile}
                                            disabled={savingProfile}
                                        >
                                            {savingProfile ? 'Salvando...' : 'Salvar'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="stdpgpro-info-grid">
                                    <div className="stdpgpro-info-item">
                                        <span className="stdpgpro-info-label">Celular / WhatsApp</span>
                                        <span className="stdpgpro-info-value">{academicProfile.phone || 'Não informado'}</span>
                                    </div>
                                    <div className="stdpgpro-info-item">
                                        <span className="stdpgpro-info-label">Data de Nascimento</span>
                                        <span className="stdpgpro-info-value">{formatDate(academicProfile.birthDate)}</span>
                                    </div>
                                    <div className="stdpgpro-info-item">
                                        <span className="stdpgpro-info-label">Estado</span>
                                        <span className="stdpgpro-info-value">{academicProfile.state || 'Não informado'}</span>
                                    </div>
                                    <div className="stdpgpro-info-item">
                                        <span className="stdpgpro-info-label">Cidade</span>
                                        <span className="stdpgpro-info-value">{academicProfile.city || 'Não informado'}</span>
                                    </div>
                                    <div className="stdpgpro-info-item">
                                        <span className="stdpgpro-info-label">Curso pretendido</span>
                                        <span className="stdpgpro-info-value">{academicProfile.intendedCourse || 'Não informado'}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ABA: COMPRAS */}
                {activeTab === 'purchases' && (
                    <div className="stdpgpro-purchases-tab">
                        <div className="stdpgpro-section-card">
                            <div className="stdpgpro-section-header">
                                <h3>Minhas Compras</h3>
                            </div>
                            {loadingPurchases ? (
                                <div className="stdpgpro-loading-small">Carregando compras...</div>
                            ) : purchases.length === 0 ? (
                                <div className="stdpgpro-empty-purchases">
                                    {user?.role === 'admin' ? (
                                        <div className="stdpgpro-role-message">
                                            <h4>Acesso Administrativo</h4>
                                            <p>Você possui acesso total à plataforma GRAIA.</p>
                                        </div>
                                    ) : user?.role === 'vendor' ? (
                                        <div className="stdpgpro-role-message">
                                            <h4>Conta Vendedor</h4>
                                            <p>Você possui acesso total à plataforma GRAIA como vendedor.</p>
                                        </div>
                                    ) : (vipAccess?.active || user?.isVip) ? (
                                        <div className="stdpgpro-role-message">
                                            <h4>Conta VIP</h4>
                                            <p>Você possui acesso total à plataforma GRAIA.</p>
                                            <div className="stdpgpro-vip-expiry">
                                                <span className="stdpgpro-vip-expiry-label">Acesso:</span>
                                                <span className={`stdpgpro-vip-expiry-value ${vipAccess?.expiresAt === null ? 'lifetime' :
                                                    new Date(vipAccess?.expiresAt || '') < new Date() ? 'expired' : 'active'
                                                    }`}>
                                                    {formatVipExpiry(vipAccess?.expiresAt || null)}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p>Você ainda não possui compras.</p>
                                            <button
                                                className="stdpgpro-btn-primary"
                                                onClick={() => window.open('/marketplace', '_blank')}
                                            >
                                                Explorar Catálogo
                                            </button>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="stdpgpro-purchases-grid">
                                    {purchases.map(purchase => (
                                        <div key={purchase._id} className="stdpgpro-purchase-card">
                                            <div className="stdpgpro-purchase-header">
                                                <h4>{purchase.productId.title}</h4>
                                                {getStatusBadge(purchase.status)}
                                            </div>
                                            <p className="stdpgpro-purchase-description">{purchase.productId.description}</p>
                                            <div className="stdpgpro-purchase-details">
                                                <div className="stdpgpro-detail-row">
                                                    <span>Produto:</span>
                                                    <span>{getProductTypeLabel(purchase.productId.type)}</span>
                                                </div>
                                                <div className="stdpgpro-detail-row">
                                                    <span>Acesso:</span>
                                                    <span>{purchase.accessType === 'lifetime' ? 'Vitalício' : 'Por período'}</span>
                                                </div>
                                                <div className="stdpgpro-detail-row">
                                                    <span>Comprado em:</span>
                                                    <span>{formatDate(purchase.createdAt)}</span>
                                                </div>
                                                {purchase.accessType === 'subscription' && (
                                                    <div className="stdpgpro-detail-row">
                                                        <span>Expira em:</span>
                                                        <span>{formatDate(purchase.expiresAt)}</span>
                                                    </div>
                                                )}
                                                {purchase.daysUntilExpiry !== null && purchase.daysUntilExpiry > 0 && (
                                                    <div className="stdpgpro-detail-row warning">
                                                        <span>Faltam {purchase.daysUntilExpiry} dias para expirar</span>
                                                    </div>
                                                )}
                                            </div>
                                            {purchase.status === 'active' && (
                                                <button
                                                    className="stdpgpro-btn-primary full"
                                                    onClick={() => {
                                                        const { type, refId } = purchase.productId;
                                                        if (type === 'access_all') {
                                                            navigate('/dashboard');
                                                        } else if (type === 'cycle' && refId) {
                                                            navigate(`/cycles/${refId}/decks`);
                                                        } else if (type === 'deck' && refId) {
                                                            navigate(`/cycles/${refId}/decks`);
                                                        } else {
                                                            navigate('/dashboard');
                                                        }
                                                    }}
                                                >
                                                    {purchase.productId.type === 'access_all' ? 'Ir para Dashboard' : 'Acessar Conteúdo'}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ABA: SEGURANÇA */}
                {activeTab === 'security' && (
                    <div className="stdpgpro-security-tab">
                        <div className="stdpgpro-section-card">
                            <div className="stdpgpro-section-header">
                                <h3>Informações da Conta</h3>
                            </div>
                            <div className="stdpgpro-info-grid">
                                <div className="stdpgpro-info-item">
                                    <span className="stdpgpro-info-label">Email</span>
                                    <span className="stdpgpro-info-value">{user?.email}</span>
                                </div>
                                <div className="stdpgpro-info-item">
                                    <span className="stdpgpro-info-label">Método de Login</span>
                                    <span className="stdpgpro-info-value">
                                        {user?.authProvider === 'google' || user?.authProvider === 'local+google'
                                            ? 'Google'
                                            : 'Email e Senha'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="stdpgpro-section-card">
                            <div className="stdpgpro-section-header">
                                <h3>Gerenciamento de Senha</h3>
                            </div>
                            <p className="stdpgpro-section-description">
                                {userAuthProvider === 'google' || userAuthProvider === 'local+google'
                                    ? 'Sua conta está vinculada ao Google. Para gerenciar sua senha, acesse as configurações da sua conta Google.'
                                    : 'Altere ou redefina sua senha de acesso à plataforma.'}
                            </p>
                            <button
                                className="stdpgpro-btn-security"
                                onClick={() => navigate('/change-password')}
                                disabled={userAuthProvider === 'google' || userAuthProvider === 'local+google'}
                            >
                                <Lock size={16} />
                                Alterar Senha
                            </button>
                        </div>

                        <div className="stdpgpro-section-card">
                            <div className="stdpgpro-section-header">
                                <h3>Dispositivos Conectados</h3>
                            </div>
                            <p className="stdpgpro-section-description">
                                Veja todos os dispositivos onde sua conta está ou esteve conectada e gerencie suas sessões ativas.
                            </p>
                            <button
                                className="stdpgpro-btn-security"
                                onClick={() => navigate('/sessions')}
                            >
                                <Monitor size={16} />
                                Ver Dispositivos Conectados
                            </button>
                        </div>

                        {(user?.role === 'admin' || user?.role === 'vendor') && (
                            <div className="stdpgpro-section-card">
                                <div className="stdpgpro-section-header">
                                    <h3>Autenticação de Dois Fatores (MFA)</h3>
                                </div>
                                <p className="stdpgpro-section-description">
                                    Adicione uma camada extra de segurança à sua conta com autenticação de dois fatores.
                                </p>
                                <MFAStatusSection />
                                <button
                                    className="stdpgpro-btn-security"
                                    onClick={() => navigate('/mfa/setup')}
                                >
                                    <Shield size={16} />
                                    {mfaStatus?.enabled ? 'Gerenciar MFA' : 'Configurar MFA'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentAreaPage;
